import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { formatStreamLabel, formatStreamShortLabel } from './formatters';
import { DBStreamSchema, StreamSchema } from './schemas';

type DBStream = z.infer<typeof DBStreamSchema>;
type Stream = z.infer<typeof StreamSchema>;

export const StreamCodecConfig: TransformerConfig<typeof StreamSchema, typeof DBStreamSchema> = {
  name: 'Stream',
  clientSchema: StreamSchema,
  dbSchema: DBStreamSchema,
  transformers: [
    // label and shortLabel restate the cohort fields, so they are composed on
    // the way out and never written back
    createTransformer<typeof StreamSchema, typeof DBStreamSchema>({
      encoder: {
        sourceFields: ['label', 'shortLabel'] as const,
        encode: () => ({}),
      },
      decoder: {
        sourceFields: ['gradYear', 'type', 'groupName', 'groupCode'] as const,
        decode: parts => {
          const named = parts as Pick<DBStream, 'gradYear' | 'type' | 'groupName' | 'groupCode'>;

          return {
            ...named,
            label: formatStreamLabel(named),
            shortLabel: formatStreamShortLabel(named),
          };
        },
      },
    }),

    // slackIds: [{ label, value }] ↔ slackIds: { value: { label } }
    createTransformer<typeof StreamSchema, typeof DBStreamSchema>({
      encoder: {
        sourceFields: ['slackIds'] as const,
        encode: ({ slackIds }) => ({
          slackIds: Object.fromEntries(
            (slackIds as Stream['slackIds']).map(slack => [slack.value, { label: slack.label }])
          ),
        }),
      },
      decoder: {
        sourceFields: ['slackIds'] as const,
        decode: ({ slackIds }) => ({
          slackIds: Object.entries(slackIds as DBStream['slackIds']).map(([value, data]) => ({
            label: data.label,
            value,
          })),
        }),
      },
    }),

    // basePermissions: [permissionId] ↔ basePermissions: { permissionId: {} }
    createTransformer<typeof StreamSchema, typeof DBStreamSchema>({
      encoder: {
        sourceFields: ['basePermissions'] as const,
        encode: ({ basePermissions }) => ({
          basePermissions: Object.fromEntries(
            (basePermissions as Stream['basePermissions']).map(permissionId => [permissionId, {}])
          ),
        }),
      },
      decoder: {
        sourceFields: ['basePermissions'] as const,
        decode: ({ basePermissions = {} }) => ({
          basePermissions: Object.keys(basePermissions),
        }),
      },
    }),

    // active: boolean ↔ active: 'true' | 'false'
    createTransformer<typeof StreamSchema, typeof DBStreamSchema>({
      encoder: {
        sourceFields: ['active'] as const,
        encode: ({ active }) => ({
          active: (active as Stream['active']) ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['active'] as const,
        decode: ({ active }) => ({
          active: (active as DBStream['active']) === 'true',
        }),
      },
    }),
  ],
};

export const encodeStreamToDb = createEncodeToDbFn(StreamCodecConfig);
export const decodeStreamFromDb = createDecodeFromDbFn(StreamCodecConfig);
